<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: 'Email Accounts', to: '/settings/emails' }
    ]"
  >
    <template #right>
      <Button
        v-if="$can('manage_communication_settings')"
        label="New Email Account"
        severity="primary"
        icon="plus"
        @click="newEmailAccount"
      />
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="EmailAccounts"
      :value="emailAccounts"
      :loading="isLoading"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-email-accounts"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name']"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.id }}</div>
        </template>
      </Column>

      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="whitespace-nowrap">{{ data.name }}</div>
        </template>
      </Column>

      <Column
        field="callCenter.id"
        :header="$t('ui.entities.departments.callCenter')"
        hidden
      ></Column>

      <Column
        field="callCenter.name"
        header="Call Center"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            :key="index"
            :label="data.callCenter.name"
            size="sm"
            class="link-hover"
            @click="$router.push('/settings/call-centers/' + data.callCenter.id)"
          />
        </template>
      </Column>

      <Column
        field="emailAddress"
        header="Email Address"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ data.emailAddress }}</div>
        </template>
      </Column>

      <Column
        field="isActive"
        header="Active"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.isActive"
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="success"
          >
            Active
          </Chip>
          <Chip
            v-else
            :rounded="false"
            outlined
            size="sm"
            dot
            severity="danger"
          >
            Disabled
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="[
              { value: true, label: 'Active' },
              { value: false, label: 'Disabled' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="createdAt"
        header="Created At"
        :sortable="true"
      >
        <template #body="{ data }">
          <div>{{ dayjs(data.createdAt).format('ll') }}</div>
        </template>
      </Column>

      <Column
        v-if="$can('manage_communication_settings')"
        :sortable="false"
      >
        <template #body="{ data }">
          <div>
            <Button
              v-tippy="$t('ui.actions.edit')"
              icon="cog"
              plain
              severity="secondary"
              @click="editEmailAccountSettings(data.id)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <teleport to="#modals-container">
    <Modal
      :is-open="editEmailAccountModalIsOpen"
      title="Edit Email Account"
      icon="envelope"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="editEmailAccountHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="editEmailAccountApiRequest.isLoading.value"
      min-width="400"
      overflow-behaviour="visible"
      :disable-close="editEmailAccountApiRequest.isLoading.value"
      @close-modal="((editEmailAccountModalIsOpen = false), (editEmailAccountHasUnsavedChanges = false), v$.$reset())"
      @ok-modal="updateEmailAccountSettings()"
    >
      <div>
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="v$.name.required?.$invalid"
        >
          <Input
            v-model="selectedEmailAccountState.name"
            :has-error="v$.name.$error"
          />
          <InputError :has-error="v$.name.$error">{{ v$.name.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.departments.callCenter')"
          :is-required="v$.callCenterId.required?.$invalid"
        >
          <Select
            v-model="selectedEmailAccountState.callCenterId"
            :options="callCentersList"
            value-key="id"
            label-key="name"
            :has-error="v$.callCenterId.$error"
          />
          <InputError :has-error="v$.callCenterId.$error">{{ v$.callCenterId.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Email Address"
          :is-required="v$.emailAddress.required?.$invalid"
        >
          <Input
            v-model="selectedEmailAccountState.emailAddress"
            :has-error="v$.emailAddress.$error"
          />
          <InputError :has-error="v$.emailAddress.$error">{{ v$.emailAddress.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Greeting Placeholder"
          :is-required="v$.placeholderGreeting.required?.$invalid"
        >
          <Input
            v-model="selectedEmailAccountState.placeholderGreeting"
            placeholder="Default"
            :has-error="v$.placeholderGreeting.$error"
          />
          <InputError :has-error="v$.placeholderGreeting.$error">
            {{ v$.placeholderGreeting.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="On Behalf of Placeholder"
          :is-required="v$.placeholderOnBehalfOf.required?.$invalid"
        >
          <Input
            v-model="selectedEmailAccountState.placeholderOnBehalfOf"
            placeholder="Default"
            :has-error="v$.placeholderOnBehalfOf.$error"
          />
          <InputError :has-error="v$.placeholderOnBehalfOf.$error">
            {{ v$.placeholderOnBehalfOf.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="Sign Off Placeholder"
          :is-required="v$.placeholderSignOff.required?.$invalid"
        >
          <TextArea
            v-model="selectedEmailAccountState.placeholderSignOff"
            placeholder="Default"
            rows="2"
            :has-error="v$.placeholderSignOff.$error"
          />
          <InputError :has-error="v$.placeholderSignOff.$error">
            {{ v$.placeholderSignOff.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </Modal>

    <Modal
      :is-open="newEmailAccountModalIsOpen"
      title="New Email Account"
      icon="envelope"
      :buttons="['ok', 'close']"
      :has-unsaved-changes="newEmailAccountHasUnsavedChanges"
      close-text="Cancel"
      ok-text="Save"
      :show-loader-on-confirm="true"
      :is-confirming="false"
      min-width="400"
      overflow-behaviour="visible"
      @close-modal="((newEmailAccountModalIsOpen = false), (newEmailAccountHasUnsavedChanges = false), v$new.$reset())"
      @ok-modal="submitNewEmailAccount()"
    >
      <div>
        <FormGroup
          :label="$t('ui.common.name')"
          :is-required="v$new.name.required?.$invalid"
        >
          <Input
            v-model="newEmailAccountState.name"
            :has-error="v$new.name.$error"
          />
          <InputError :has-error="v$new.name.$error">{{ v$new.name.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.departments.callCenter')"
          :is-required="v$new.callCenterId.required?.$invalid"
        >
          <Select
            v-model="newEmailAccountState.callCenterId"
            :options="callCentersList"
            value-key="id"
            label-key="name"
            :has-error="v$new.callCenterId.$error"
          />
          <InputError :has-error="v$new.callCenterId.$error">{{ v$new.callCenterId.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Email Address"
          :is-required="v$new.emailAddress.required?.$invalid"
        >
          <Input
            v-model="newEmailAccountState.emailAddress"
            :has-error="v$new.emailAddress.$error"
          />
          <InputError :has-error="v$new.emailAddress.$error">{{ v$new.emailAddress.$errors[0]?.$message }}</InputError>
        </FormGroup>

        <FormGroup
          label="Greeting Placeholder"
          :is-required="v$new.placeholderGreeting.required?.$invalid"
        >
          <Input
            v-model="newEmailAccountState.placeholderGreeting"
            placeholder="Default"
            :has-error="v$new.placeholderGreeting.$error"
          />
          <InputError :has-error="v$new.placeholderGreeting.$error">
            {{ v$new.placeholderGreeting.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="On Behalf of Placeholder"
          :is-required="v$new.placeholderOnBehalfOf.required?.$invalid"
        >
          <Input
            v-model="newEmailAccountState.placeholderOnBehalfOf"
            placeholder="Default"
            :has-error="v$new.placeholderOnBehalfOf.$error"
          />
          <InputError :has-error="v$new.placeholderOnBehalfOf.$error">
            {{ v$new.placeholderOnBehalfOf.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <FormGroup
          label="Sign Off Placeholder"
          :is-required="v$new.placeholderSignOff.required?.$invalid"
        >
          <TextArea
            v-model="newEmailAccountState.placeholderSignOff"
            placeholder="Default"
            rows="2"
            :has-error="v$new.placeholderSignOff.$error"
          />
          <InputError :has-error="v$new.placeholderSignOff.$error">
            {{ v$new.placeholderSignOff.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </Modal>
  </teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Modal from '@/components/modal/Modal.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Select from '@/components/forms/Select.vue'
import Chip from '@/components/chip/Chip.vue'
import TextArea from '@/components/forms/TextArea.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from 'primevue/api'
import { alertToast } from '@/utilities/notification'

import useApiRequest from '@/composables/useApiRequest'
import useVuelidate from '@vuelidate/core'
import { required, helpers, email } from '@vuelidate/validators'

const emailAccounts = ref(null)
const emailAccountsApiRequest = useApiRequest()
const editEmailAccountApiRequest = useApiRequest()
const newEmailAccountApiRequest = useApiRequest()
const isLoading = emailAccountsApiRequest.isLoading

const editEmailAccountModalIsOpen = ref(false)
const editEmailAccountHasUnsavedChanges = ref(false)
const selectedEmailAccountInitState = ref(null)
const selectedEmailAccountState = ref(null)

const newEmailAccountModalIsOpen = ref(false)
const newEmailAccountHasUnsavedChanges = ref(false)
const newEmailAccountInitState = ref(null)
const newEmailAccountState = ref(null)
const filters = ref([])

const callCentersList = ref()
const callCentersRequest = useApiRequest()

function fetchEmailAccounts() {
  emailAccountsApiRequest
    .send({ endpoint: '/v1/comms/email-accounts' })
    .then(response => {
      if (response.success && !response.errors) {
        emailAccounts.value = response.data
      } else {
        alertToast('Error', response.message, 'error')
      }
    })
    .catch(err => {
      alertToast('Error', err.message, 'error')
    })
}
fetchEmailAccounts()

const emailAccountsList = computed(() => {
  return emailAccounts.value?.length
    ? emailAccounts.value.map(acc => {
        return acc.emailAddress
      })
    : []
})

try {
  callCentersRequest.send({ endpoint: '/v1/call-centers' }).then(response => {
    if (response.success) {
      callCentersList.value = response.data
    } else {
      alertToast('Error', response.message, 'error')
    }
  })
} catch (err) {
  alertToast('Error', err.message, 'error')
}

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.IN },
    isActive: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-settings-email-accounts')
  initFilters()
}

function editEmailAccountSettings(id) {
  const emailToEdit = emailAccounts.value.find(account => account.id == id)
  // store the email address for vuelidate check
  emailAddressToEdit.value = emailToEdit.emailAddress

  selectedEmailAccountInitState.value = {
    id: emailToEdit.id,
    name: emailToEdit.name,
    emailAddress: emailToEdit.emailAddress,
    placeholderGreeting: emailToEdit.placeholderGreeting,
    placeholderOnBehalfOf: emailToEdit.placeholderOnBehalfOf,
    placeholderSignOff: emailToEdit.placeholderSignOff,
    callCenterId: emailToEdit.callCenter.id
  }

  selectedEmailAccountState.value = { ...selectedEmailAccountInitState.value }

  if (selectedEmailAccountState.value) {
    editEmailAccountModalIsOpen.value = true
  } else {
    alertToast('Error', 'Could not find email account', 'error')
  }
}

function newEmailAccount() {
  newEmailAccountInitState.value = {
    name: null,
    emailAddress: null,
    callCenterId: null,
    placeholderGreeting: null,
    placeholderOnBehalfOf: null,
    placeholderSignOff: null
  }

  newEmailAccountState.value = { ...newEmailAccountInitState.value }
  newEmailAccountModalIsOpen.value = true
}

const emailAddressToEdit = ref(null)

const editEmailInputRules = {
  name: { required },
  emailAddress: {
    required,
    email: helpers.withMessage('Invalid email address', email),
    isAvailable: helpers.withMessage('Email address is already being used', value => {
      if (emailAccountsList.value.includes(value) && value != emailAddressToEdit.value) {
        return false
      } else {
        return true
      }
    })
  },
  callCenterId: { required },
  placeholderGreeting: {},
  placeholderOnBehalfOf: {},
  placeholderSignOff: {}
}

const newEmailInputRules = {
  name: { required },
  emailAddress: {
    required,
    email: helpers.withMessage('Invalid email address', email),
    isAvailable: helpers.withMessage('Email address is already being used', value => {
      return !emailAccountsList.value.includes(value)
    })
  },
  callCenterId: { required },
  placeholderGreeting: {},
  placeholderOnBehalfOf: {},
  placeholderSignOff: {}
}

const v$ = useVuelidate(editEmailInputRules, selectedEmailAccountState, { $stopPropagation: true })
const v$new = useVuelidate(newEmailInputRules, newEmailAccountState)

async function updateEmailAccountSettings() {
  const isFormCorrect = await v$.value.$validate()

  if (!isFormCorrect) {
    return
  }

  const dataToPost = {
    name: selectedEmailAccountState.value.name,
    emailAddress: selectedEmailAccountState.value.emailAddress,
    placeholderGreeting: selectedEmailAccountState.value.placeholderGreeting,
    placeholderOnBehalfOf: selectedEmailAccountState.value.placeholderOnBehalfOf,
    placeholderSignOff: selectedEmailAccountState.value.placeholderSignOff,
    callCenterId: selectedEmailAccountState.value.callCenterId
  }

  try {
    editEmailAccountApiRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/comms/email-accounts/${selectedEmailAccountState.value.id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to update email account', 'error')
        } else {
          alertToast('Success!', 'Email account details updated', 'success')

          editEmailAccountModalIsOpen.value = false
          editEmailAccountHasUnsavedChanges.value = false
          fetchEmailAccounts()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$.value.$reset()
}

async function submitNewEmailAccount() {
  const isFormCorrect = await v$new.value.$validate()
  if (!isFormCorrect) {
    return
  }

  const dataToPost = { ...newEmailAccountState.value }
  try {
    newEmailAccountApiRequest
      .send({
        method: 'POST',
        endpoint: '/v1/comms/email-accounts',
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (!response.success) {
          alertToast('Error', 'Failed to add email account', 'error')
        } else {
          alertToast('Success!', 'Email account added', 'success')
          newEmailAccountModalIsOpen.value = false
          newEmailAccountHasUnsavedChanges.value = false
          fetchEmailAccounts()
        }
      })
  } catch (err) {
    alertToast('Error', err.message, 'error')
  }
  v$new.value.$reset()
}

watch(
  selectedEmailAccountState,
  newValue => {
    // Compare the current state with the initial state
    editEmailAccountHasUnsavedChanges.value =
      JSON.stringify(newValue) !== JSON.stringify(selectedEmailAccountInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)

watch(
  newEmailAccountState,
  newValue => {
    // Compare the current state with the initial state
    newEmailAccountHasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(newEmailAccountInitState.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)
</script>

<style></style>
